import React, { useMemo } from "react";
import { useEffect, useState } from "react";
import { Autocomplete, Box, TextField } from "@mui/material";
import { styled, lighten, darken } from '@mui/system';
import { throttle } from "lodash";
import APIRequest from "system/APIRequest";
import { QueryValueInputProps } from "./QueryValueInput";
import { useTranslation } from "react-i18next";
import { QueryFieldOption } from "system/query/QueryInfo";

const GroupHeader = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: '-8px',
  padding: '4px 10px',
  color: theme.palette.primary.main,
  backgroundColor: lighten(theme.palette.primary.light, 0.85),
  ...theme.applyStyles('dark', {
    backgroundColor: darken(theme.palette.primary.main, 0.8),
  }),
}));

export function QueryDynamicOptionsInput(props: QueryValueInputProps): JSX.Element {
  const { i18n } = useTranslation();

  const [inputValue, setInputValue] = useState<string>('');
  const [searchOptions, setSearchOptions] = useState<QueryFieldOption[]>([]);

  const datasetId:string = props.queryInfo?.dataset?.id;
  const fieldId:string = props.selectedField?.id || "";

  const suggestions = useMemo(() => {
    const field = props.queryInfo?.fields.find(field => field.id === fieldId);
    if (!fieldId || !field) {
      return;
    }
    const uniqueSuggestions = new Map<string, QueryFieldOption>();
    field.suggestions?.forEach((suggestion: QueryFieldOption) => {
      if (typeof suggestion === "string") {
        uniqueSuggestions.set(suggestion, {id: suggestion, label: suggestion, _is_suggestion: true});
      } else if (suggestion?.id) {
        uniqueSuggestions.set(suggestion?.label, {...suggestion, _is_suggestion: true});
      }
    });
    return Array.from(uniqueSuggestions.values());
  }, [fieldId, props.queryInfo]);

  const keywordOptions = useMemo(() => {
    // Make sure we have options and that the selected value and label are in the options
    const allOptions = new Map<string, QueryFieldOption>()

    // Add any search options to the all options
    if (searchOptions && searchOptions.length) {
      searchOptions.forEach((option: QueryFieldOption) => {
        allOptions.set(option.id + "", option);
      });
    }

    // Add the suggestions to the all options
    if (suggestions && suggestions.length) {
      suggestions.forEach((option: QueryFieldOption) => {
        allOptions.set(option.id + "", option);
      });
    }

    // If the selected option is not in the options, add it to the options if it matches the input value
    if (props.value && allOptions.has(props.value + "") === false) {
      allOptions.set(props.value + "", {
        id: props.value + "",
        label: props.label || i18n.t("null"),
        _is_suggestion: false
      });
    }

    let uniqueOptions = Array.from(allOptions.values());

    // Remove any options that do not match the input value
    if (inputValue && inputValue.length > 0) {
      uniqueOptions = uniqueOptions.filter((option: QueryFieldOption) => {
        if (option.label && option.label.toLowerCase().includes(inputValue.toLowerCase())) {
          return true;
        }
        if (option.id && option.id.toLowerCase().includes(inputValue.toLowerCase())) {
          return true;
        }
        return false;
      });
    }

    // Sort the options by label, and put suggestions first
    uniqueOptions = uniqueOptions.sort((a: QueryFieldOption, b: QueryFieldOption) => {
      if (a._is_suggestion && !b._is_suggestion) {
        return -1;
      } else if (!a._is_suggestion && b._is_suggestion) {
        return 1;
      } else if (a.label && b.label) {
        return a.label.localeCompare(b.label);
      } else if (a.id && b.id) {
        return a.id.localeCompare(b.id);
      }
    });

    return uniqueOptions;
  }, [props.value, props.label, suggestions, searchOptions, inputValue]);

  function autocompleteSearch(searchString:string) {
    if (searchString.length < 2) {
      setSearchOptions([]);
      return;
    }

    APIRequest(`suggest/${datasetId}`, {
      field: fieldId,
      input: searchString,
      size: '50'
    }).then((response:any) => {
      const options = response.suggestions.map((option: QueryFieldOption) => {
        if (option.id === "__null__" || option.id === null) {
          return {id: "__null__", label: i18n.t("null")};
        }
        return option;
      });
      setSearchOptions(options);
    });
  }

  const searchThrottled = throttle(autocompleteSearch, 500);

  useEffect(() => {
    searchThrottled(inputValue);
  }, [inputValue]);

  const selectedValue = keywordOptions.find(field => {
    return field.id === props.value || field.label === inputValue;
  }) || null;

  return (
    <Autocomplete
      freeSolo={!!props.selectedField?.suggestions}
      getOptionLabel={(option) => {
        if (typeof option === 'string') {
          return option;
        } else if (option.label) {
          return option.label
        } else {
          return i18n.t("null");
        }
      }}
      groupBy={(option) => option._is_suggestion ? i18n.t("suggestions") : i18n.t("results")}
      renderGroup={(params) => {
        return (
          <li key={params.group}>
            <GroupHeader>{params.group}</GroupHeader>
            <ul>{params.children}</ul>
          </li>
        );
      }}
      onKeyDown= {(e) => {
        // Prevent dialogs from overriding the input
        e.stopPropagation();
      }}
      filterOptions={(x) => x}
      options={keywordOptions}
      includeInputInList
      filterSelectedOptions
      inputValue={inputValue || ""}
      isOptionEqualToValue={(option, value) => {
        return option.id === value.id;
      }}
      value={selectedValue}
      onChange={(event: React.SyntheticEvent<Element, Event>, newValue: any) => {
        if (newValue) {
          props.setValue(newValue.id, newValue.label);
          if (props.setLabel) {
            props.setLabel(newValue.label);
          }
        } else {
          props.setValue(null, i18n.t("null"));
          if (props.setLabel) {
            props.setLabel(i18n.t("null"));
          }
        }
      }}
      onInputChange={(event: React.SyntheticEvent<Element, Event>, newInputValue: string) => {
        setInputValue(newInputValue || "");
        if (newInputValue && newInputValue.length > 0) {
          // find the option that matches the input value
          const option = keywordOptions.find((option: QueryFieldOption) => {
            return option.label === newInputValue || option.id === newInputValue;
          });
          if (option) {
            props.setValue(option.id, option.label);
            if (props.setLabel) {
              props.setLabel(option.label);
            }
          } else {
            props.setValue(null, i18n.t("null"));
          }

          if (option === undefined && inputValue.length > 0) {
            props.setValue(newInputValue)
            props.setLabel && props.setLabel(newInputValue);
          }
        } else {
          props.setValue(null, i18n.t("null"));
          if (props.setLabel) {
            props.setLabel(i18n.t("null"));
          }
        }
      }}
      renderInput={(params) => {
        return (<TextField autoComplete="off" {...params} />);
      }}
    />
  );
}
